import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { CalendarRange, Plus, Trash2, Save, GripVertical, Link2, BookmarkPlus, Wand2, FileDown, FileUp, Library, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import useJobConfig from '../../hooks/useJobConfig';
import DateField from '../DateField';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generate unique ID for segments
const generateSegmentId = () => `seg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

export default function SchedulePlanSection({ jobId, segments: initialSegments, jobEndDate, canModify, onReload }) {
  const { user } = useAuth();
  const isManagement = user?.role === 'management';
  // Org-configured statuses (defaults + custom, incl. ones created by MS Project imports)
  const { visibleStatuses, hexForStatus, knownStatus, refresh } = useJobConfig();
  const STATUS_OPTIONS = visibleStatuses.map(s => ({ value: s.value, label: s.label, color: hexForStatus(s.value) }));
  const NAMED_COLORS = [
    ['slate', '#64748B'], ['red', '#EF4444'], ['orange', '#F97316'], ['amber', '#F59E0B'],
    ['yellow', '#EAB308'], ['lime', '#84CC16'], ['green', '#22C55E'], ['emerald', '#10B981'],
    ['teal', '#14B8A6'], ['cyan', '#06B6D4'], ['sky', '#0EA5E9'], ['blue', '#3B82F6'],
    ['indigo', '#6366F1'], ['violet', '#8B5CF6'], ['purple', '#A855F7'], ['pink', '#EC4899'],
    ['rose', '#F43F5E'],
  ].map(([name, hex]) => ({ name, hex }));

  const saveStatusColor = async (statusValue, color) => {
    try {
      await axios.patch(`${API}/settings/job-statuses/${statusValue}`, { color });
      refresh();
      toast.success('Status color updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update color');
    }
  };
  const colorDebounceRef = useRef(null);
  const saveStatusColorDebounced = (statusValue, color) => {
    clearTimeout(colorDebounceRef.current);
    colorDebounceRef.current = setTimeout(() => saveStatusColor(statusValue, color), 500);
  };
  // Add IDs to initial segments if they don't have them
  const [segments, setSegments] = useState(() => 
    (initialSegments || []).map(seg => ({ ...seg, _id: seg._id || generateSegmentId() }))
  );
  const [dirty, setDirty] = useState(false);
  const [customEnd, setCustomEnd] = useState('');
  const [extending, setExtending] = useState(false);
  // Pull in server-side changes (e.g. a date edited on the job header cascaded
  // through the plan) unless there are unsaved edits on screen.
  const initialKey = JSON.stringify(initialSegments || []);
  useEffect(() => {
    if (dirty) return;
    setSegments((initialSegments || []).map((seg) => ({ ...seg, _id: seg._id || generateSegmentId() })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialKey]);
  const [dragIdx, setDragIdx] = useState(null);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const importRef = useRef(null);
  const [browseOpen, setBrowseOpen] = useState(false);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [applyingTplId, setApplyingTplId] = useState(null);

  const fetchSavedTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const res = await axios.get(`${API}/schedule-templates`);
      setSavedTemplates(res.data);
    } catch {
      toast.error('Failed to load templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const openBrowse = () => {
    setBrowseOpen(true);
    fetchSavedTemplates();
  };

  const applySavedTemplate = async (templateId) => {
    setApplyingTplId(templateId);
    try {
      const res = await axios.post(`${API}/jobs/${jobId}/apply-template`, { template_id: templateId });
      toast.success(`Template applied — ${res.data.segments.length} phases scheduled${res.data.end_date_extended ? ' (job end date extended)' : ''}`);
      setBrowseOpen(false);
      if (onReload) onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply template');
    } finally {
      setApplyingTplId(null);
    }
  };

  const deleteSavedTemplate = async (templateId) => {
    try {
      await axios.delete(`${API}/schedule-templates/${templateId}`);
      setSavedTemplates(prev => prev.filter(t => t.id !== templateId));
      toast.success('Template deleted');
    } catch {
      toast.error('Failed to delete template');
    }
  };

  const exportMsProject = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/jobs/${jobId}/msproject-export`, { responseType: 'blob' });
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^";]+)"?/);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = m ? m[1] : 'Job_schedule.xml';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('MS Project file downloaded');
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const importMsProject = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const tpl = await axios.post(`${API}/schedule-templates/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const applied = await axios.post(`${API}/jobs/${jobId}/apply-template`, { template_id: tpl.data.id });
      toast.success(`"${tpl.data.name}" imported and applied — ${applied.data.segments.length} phases${applied.data.end_date_extended ? ' (job end date extended)' : ''}`);
      if (onReload) onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  // Remap depends_on indexes after a single-step move from index `from` to `to`
  const remapDeps = (list, from, to) => {
    const newIndex = (i) => {
      if (i === from) return to;
      if (from < to && i > from && i <= to) return i - 1;
      if (to < from && i >= to && i < from) return i + 1;
      return i;
    };
    return list.map(seg => ({
      ...seg,
      depends_on: (seg.depends_on || []).map(newIndex),
    }));
  };

  const handleDragStart = (idx) => {
    setDragIdx(idx);
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    let updated = [...segments];
    const [moved] = updated.splice(dragIdx, 1);
    updated.splice(idx, 0, moved);
    updated = remapDeps(updated, dragIdx, idx);
    setSegments(updated);
    setDragIdx(idx);
    setDirty(true);
  };

  const handleDragEnd = () => {
    setDragIdx(null);
  };

  const addSegment = () => {
    // Default: next day after last segment's end, or today
    const last = segments[segments.length - 1];
    let startDate = fmtDay(new Date());
    if (last?.end_date) {
      startDate = fmtDay(addDays(parseDay(last.end_date), 1));
    }
    const endDate = startDate;
    // Guess next status in sequence
    const usedStatuses = segments.map(s => s.status);
    const nextStatus = STATUS_OPTIONS.find(o => !usedStatuses.includes(o.value))?.value || 'intake';
    setSegments([...segments, { _id: generateSegmentId(), status: nextStatus, start_date: startDate, end_date: endDate }]);
    setDirty(true);
  };

  // --- Date helpers (local-safe: no UTC drift) ------------------------------
  const parseDay = (s) => new Date(`${s}T00:00:00`);
  const fmtDay = (d) => {
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  };
  const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
  const daysBetween = (a, b) => Math.round((parseDay(b) - parseDay(a)) / 86400000);

  const depsOf = (list, i) =>
    (list[i].depends_on || []).filter((d) => d >= 0 && d < list.length && d !== i);

  // Push every segment that depends (directly or through the chain) on a changed
  // segment, keeping both its original duration and its original gap after the
  // dependency — so "the day after parts arrive" stays the day after.
  const cascadeDependents = (prev, next, changedIdx) => {
    const durations = prev.map((s) => Math.max(1, daysBetween(s.start_date, s.end_date) + 1));
    const gaps = prev.map((s, i) => {
      const deps = depsOf(prev, i);
      if (!deps.length) return null;
      const latest = deps.reduce((acc, d) => (prev[d].end_date > acc ? prev[d].end_date : acc), prev[deps[0]].end_date);
      return daysBetween(latest, s.start_date);
    });

    const list = [...next];
    const changed = new Set([changedIdx]);
    let guard = 0;
    let progressed = true;
    while (progressed && guard++ < list.length + 5) {
      progressed = false;
      list.forEach((seg, i) => {
        const deps = depsOf(list, i);
        if (!deps.length || !deps.some((d) => changed.has(d))) return;
        const latestEnd = deps.reduce((acc, d) => (list[d].end_date > acc ? list[d].end_date : acc), list[deps[0]].end_date);
        const gap = gaps[i] === null || gaps[i] === undefined ? 1 : Math.max(0, gaps[i]);
        const start = addDays(parseDay(latestEnd), gap);
        const end = addDays(start, durations[i] - 1);
        const ns = fmtDay(start);
        const ne = fmtDay(end);
        changed.add(i);
        if (ns !== seg.start_date || ne !== seg.end_date) {
          list[i] = { ...seg, start_date: ns, end_date: ne };
          progressed = true;
        }
      });
    }
    return list;
  };

  const updateSegment = (idx, field, value) => {
    const updated = [...segments];
    updated[idx] = { ...updated[idx], [field]: value };
    // Picking a real status replaces any imported task name
    if (field === 'status') updated[idx].label = null;

    if ((field === 'start_date' || field === 'end_date') && value) {
      const prevSeg = segments[idx];
      if (field === 'start_date' && prevSeg.start_date && prevSeg.end_date) {
        // Moving the start keeps the segment's length — the end follows it.
        const duration = Math.max(1, daysBetween(prevSeg.start_date, prevSeg.end_date) + 1);
        updated[idx].end_date = fmtDay(addDays(parseDay(value), duration - 1));
      } else if (field === 'end_date' && value < updated[idx].start_date) {
        updated[idx].start_date = value;
      }
      setSegments(cascadeDependents(segments, updated, idx));
      setDirty(true);
      return;
    }

    setSegments(updated);
    setDirty(true);
  };

  const removeSegment = (idx) => {
    setSegments(segments.filter((_, i) => i !== idx).map(seg => ({
      ...seg,
      depends_on: (seg.depends_on || []).filter(d => d !== idx).map(d => (d > idx ? d - 1 : d)),
    })));
    setDirty(true);
  };

  const toggleDependency = (idx, depIdx) => {
    const updated = [...segments];
    const deps = updated[idx].depends_on || [];
    updated[idx] = {
      ...updated[idx],
      depends_on: deps.includes(depIdx) ? deps.filter(d => d !== depIdx) : [...deps, depIdx].sort((a, b) => a - b),
    };
    setSegments(updated);
    setDirty(true);
  };

  // Recalculate dates from dependencies (FS + 1 day), keeping each segment's duration
  const autoSchedule = () => {
    if (segments.length === 0) return;
    const anchor = new Date(Math.min(...segments.map(s => parseDay(s.start_date))));
    const durations = segments.map(s => Math.max(1, daysBetween(s.start_date, s.end_date) + 1));
    const starts = new Array(segments.length).fill(null);
    const ends = new Array(segments.length).fill(null);
    const fmt = fmtDay;
    let remaining = segments.map((_, i) => i);
    let prevEnd = null;
    let guard = 0;
    while (remaining.length && guard++ < segments.length * segments.length + 10) {
      let progressed = false;
      for (const i of [...remaining]) {
        const deps = depsOf(segments, i);
        if (deps.some(d => ends[d] === null)) continue;
        let start;
        if (deps.length) start = addDays(new Date(Math.max(...deps.map(d => ends[d]))), 1);
        else if (i === 0 || prevEnd === null) start = anchor;
        else start = addDays(prevEnd, 1);
        starts[i] = start;
        ends[i] = addDays(start, durations[i] - 1);
        prevEnd = ends[i];
        remaining = remaining.filter(x => x !== i);
        progressed = true;
      }
      if (!progressed) { toast.error('Dependency loop detected — fix the links first'); return; }
    }
    setSegments(segments.map((seg, i) => ({ ...seg, start_date: fmt(starts[i]), end_date: fmt(ends[i]) })));
    setDirty(true);
    toast.success('Dates recalculated from dependencies');
  };

  const saveAsTemplate = async () => {
    setSavingTemplate(true);
    try {
      if (dirty) await axios.put(`${API}/jobs/${jobId}/schedule-plan`, { segments });
      const res = await axios.post(`${API}/schedule-templates/from-job/${jobId}`, { name: templateName });
      setDirty(false);
      toast.success(`Template "${res.data.name}" saved (${res.data.tasks.length} phases)`);
      setTemplateOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const save = async () => {
    try {
      await axios.put(`${API}/jobs/${jobId}/schedule-plan`, { segments });
      setDirty(false);
      onReload();
      toast.success('Schedule plan saved');
    } catch (error) {
      toast.error('Failed to save schedule');
    }
  };

  // --- Slip detection: does any phase now finish after the job's end date? ---
  const jobEndDay = jobEndDate ? String(jobEndDate).slice(0, 10) : null;
  const latestSeg = segments.reduce(
    (acc, seg) => (seg.end_date && (!acc || seg.end_date > acc.end_date) ? seg : acc),
    null,
  );
  const slip = jobEndDay && latestSeg && latestSeg.end_date > jobEndDay
    ? { date: latestSeg.end_date, days: daysBetween(jobEndDay, latestSeg.end_date), seg: latestSeg }
    : null;

  const prettyDay = (d) => parseDay(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const applyJobEndDate = async (newDay) => {
    setExtending(true);
    try {
      const time = jobEndDate && String(jobEndDate).length > 10 ? String(jobEndDate).slice(11, 19) : '17:00:00';
      await axios.patch(`${API}/jobs/${jobId}`, { end_date: `${newDay}T${time}` });
      toast.success(`Job end date moved to ${prettyDay(newDay)}`);
      setCustomEnd('');
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update the job end date');
    } finally {
      setExtending(false);
    }
  };

  const getStatusColor = (status) => STATUS_OPTIONS.find(o => o.value === status)?.color || '#64748B';
  const segLabel = (seg) => seg.label || STATUS_OPTIONS.find(o => o.value === seg.status)?.label || seg.status;
  // Imported MS Project tasks may carry names that aren't real statuses —
  // inject them as an option so the dropdown isn't blank.
  const optionsForSegment = (seg) => {
    if (STATUS_OPTIONS.some(o => o.value === seg.status)) return STATUS_OPTIONS;
    return [{ value: seg.status, label: segLabel(seg), color: '#64748B' }, ...STATUS_OPTIONS];
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6" data-testid="schedule-plan-section">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <CalendarRange size={20} />Schedule Plan
        </h4>
        {canModify && (
          <div className="flex gap-2 flex-wrap justify-end">
            {isManagement && (
              <>
                <Button size="sm" variant="outline" onClick={openBrowse} data-testid="plan-browse-templates-btn">
                  <Library size={14} className="mr-1" />Schedule Templates
                </Button>
                <input type="file" accept=".xml,text/xml,application/xml" ref={importRef} className="hidden" onChange={importMsProject} data-testid="plan-msproject-import-input" />
                <Button size="sm" variant="outline" onClick={() => importRef.current?.click()} disabled={importing} data-testid="plan-msproject-import-btn">
                  <FileUp size={14} className="mr-1" />{importing ? 'Importing...' : 'Import MS Project'}
                </Button>
                <Button size="sm" variant="outline" onClick={exportMsProject} disabled={exporting} data-testid="plan-msproject-export-btn">
                  <FileDown size={14} className="mr-1" />{exporting ? 'Exporting...' : 'Export MS Project'}
                </Button>
              </>
            )}
            {segments.some(s => (s.depends_on || []).length > 0) && (
              <Button size="sm" variant="outline" onClick={autoSchedule} title="Recalculate dates from dependencies" data-testid="auto-schedule-btn">
                <Wand2 size={14} className="mr-1" />Auto-schedule
              </Button>
            )}
            {isManagement && segments.length > 0 && (
              <Button size="sm" variant="outline" onClick={() => { setTemplateName(''); setTemplateOpen(true); }} data-testid="save-as-template-btn">
                <BookmarkPlus size={14} className="mr-1" />Save as Template
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={addSegment} data-testid="add-segment-btn">
              <Plus size={14} className="mr-1" />Add Segment
            </Button>
            {dirty && (
              <Button size="sm" onClick={save} className="bg-slate-900" data-testid="save-schedule-btn">
                <Save size={14} className="mr-1" />Save Plan
              </Button>
            )}
          </div>
        )}
      </div>

      {slip && (
        <div
          className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4"
          data-testid="schedule-slip-warning"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-600" />
            <div className="min-w-0 flex-1">
              <p className="font-bold text-amber-900">
                Schedule slip — {slip.days} day{slip.days === 1 ? '' : 's'} past the job end date
              </p>
              <p className="mt-0.5 text-sm text-amber-800">
                <span className="font-semibold">{segLabel(slip.seg)}</span> now finishes {prettyDay(slip.date)},
                but this job is scheduled to end {prettyDay(jobEndDay)}.
              </p>
              {canModify && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    disabled={extending}
                    onClick={() => applyJobEndDate(slip.date)}
                    className="bg-amber-600 text-white hover:bg-amber-700"
                    data-testid="slip-extend-btn"
                  >
                    Move job end date to {prettyDay(slip.date)}
                  </Button>
                  <span className="text-xs font-semibold uppercase tracking-wide text-amber-700">or pick a date</span>
                  <DateField
                    value={customEnd}
                    onChange={(v) => { setCustomEnd(v); if (v) applyJobEndDate(v); }}
                    min={slip.date}
                    placeholder="Choose end date"
                    className="w-[15rem]"
                    data-testid="slip-custom-end-date"
                  />
                </div>
              )}
              {dirty && (
                <p className="mt-2 text-xs text-amber-700">
                  Save the plan to keep these phase dates.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {segments.length === 0 ? (
        <p className="text-slate-500 text-center py-6" data-testid="no-schedule">
          No schedule plan defined. Add segments to plan out the job timeline.
        </p>
      ) : (
        <div className="space-y-2" data-testid="schedule-segments">
          {/* Header */}
          <div className="grid grid-cols-[auto_2fr_1fr_1fr_auto_auto] gap-3 px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-500">
            <span className="w-6"></span>
            <span>Status</span>
            <span>Start Date</span>
            <span>End Date</span>
            <span className="w-20 text-center">Depends On</span>
            <span className="w-9"></span>
          </div>

          {segments.map((seg, idx) => (
            <div
              key={seg._id}
              draggable={canModify}
              onDragStart={() => handleDragStart(idx)}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDragEnd={handleDragEnd}
              className={`grid grid-cols-[auto_2fr_1fr_1fr_auto_auto] gap-3 items-center px-3 py-2 rounded-lg border transition-colors ${
                dragIdx === idx ? 'border-blue-400 bg-blue-50' : 'border-slate-100 hover:border-slate-200'
              }`}
              data-testid={`segment-${seg._id}`}
            >
              {canModify ? (
                <div className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600" data-testid={`drag-handle-${seg._id}`}>
                  <GripVertical size={16} />
                </div>
              ) : (
                <span className="w-6"></span>
              )}
              <div className="flex items-center gap-2">
                {isManagement && canModify && knownStatus(seg.status) ? (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="w-4 h-4 rounded-sm flex-shrink-0 ring-offset-1 hover:ring-2 hover:ring-slate-300 transition-shadow"
                        style={{ backgroundColor: getStatusColor(seg.status) }}
                        title="Edit status color"
                        data-testid={`status-color-btn-${seg._id}`}
                      />
                    </PopoverTrigger>
                    <PopoverContent className="w-60 p-3" align="start">
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 pb-2">"{segLabel(seg)}" color</p>
                      <div className="grid grid-cols-6 gap-1.5 pb-2">
                        {NAMED_COLORS.map(c => (
                          <button
                            key={c.name}
                            type="button"
                            onClick={() => saveStatusColor(seg.status, c.name)}
                            className="w-7 h-7 rounded hover:scale-110 transition-transform"
                            style={{ backgroundColor: c.hex }}
                            title={c.name}
                            data-testid={`color-swatch-${c.name}-${seg._id}`}
                          />
                        ))}
                      </div>
                      <div className="flex items-center gap-2 border-t border-slate-100 pt-2">
                        <input
                          type="color"
                          defaultValue={getStatusColor(seg.status).startsWith('#') ? getStatusColor(seg.status) : '#64748b'}
                          onChange={(e) => saveStatusColorDebounced(seg.status, e.target.value)}
                          className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent p-0 shrink-0"
                          data-testid={`custom-color-input-${seg._id}`}
                        />
                        <span className="text-xs text-slate-500">Custom color</span>
                      </div>
                      <p className="text-[11px] text-slate-400 pt-2">Applies everywhere this status is used.</p>
                    </PopoverContent>
                  </Popover>
                ) : (
                  <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: getStatusColor(seg.status) }}></div>
                )}
                {canModify ? (
                  <Select value={seg.status} onValueChange={(v) => updateSegment(idx, 'status', v)}>
                    <SelectTrigger className="h-9" data-testid={`segment-status-${seg._id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {optionsForSegment(seg).map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: opt.color }}></div>
                            {opt.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <span className="text-sm font-medium text-slate-900">
                    {segLabel(seg)}
                  </span>
                )}
              </div>
              {canModify ? (
                <>
                  <DateField
                    value={seg.start_date}
                    onChange={(v) => updateSegment(segments.indexOf(seg), 'start_date', v)}
                    data-testid={`segment-start-${seg._id}`}
                  />
                  <DateField
                    value={seg.end_date}
                    onChange={(v) => updateSegment(segments.indexOf(seg), 'end_date', v)}
                    data-testid={`segment-end-${seg._id}`}
                  />
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button size="sm" variant="outline" className="h-9 w-20 px-1 text-xs" data-testid={`segment-deps-${seg._id}`}>
                        <Link2 size={12} className="mr-1 shrink-0" />
                        {(seg.depends_on || []).length ? (seg.depends_on || []).map(d => `#${d + 1}`).join(',') : 'None'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-2" align="end">
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 px-1 pb-2">Starts after (finish-to-start)</p>
                      {segments.length < 2 ? (
                        <p className="text-xs text-slate-400 px-1">Add more segments to link them.</p>
                      ) : (
                        <div className="space-y-1 max-h-48 overflow-y-auto">
                          {segments.map((other, oIdx) => {
                            if (other._id === seg._id) return null;
                            const checked = (seg.depends_on || []).includes(oIdx);
                            return (
                              <label key={other._id} className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-slate-50 cursor-pointer text-sm" data-testid={`dep-option-${seg._id}-${oIdx}`}>
                                <input type="checkbox" checked={checked} onChange={() => toggleDependency(segments.indexOf(seg), oIdx)} className="accent-slate-900" />
                                <div className="w-2 h-2 rounded-sm shrink-0" style={{ backgroundColor: getStatusColor(other.status) }}></div>
                                <span className="truncate">#{oIdx + 1} {segLabel(other)}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => removeSegment(segments.indexOf(seg))}
                    className="text-red-600 hover:bg-red-50 h-9 w-9 p-0"
                    data-testid={`segment-delete-${seg._id}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                </>
              ) : (
                <>
                  <span className="text-sm text-slate-700">{seg.start_date}</span>
                  <span className="text-sm text-slate-700">{seg.end_date}</span>
                  <span className="w-20 text-center text-xs text-slate-500">
                    {(seg.depends_on || []).length ? (seg.depends_on || []).map(d => `#${d + 1}`).join(', ') : '—'}
                  </span>
                  <span className="w-9"></span>
                </>
              )}
            </div>
          ))}

          {/* Visual timeline preview */}
          <div className="mt-4 pt-4 border-t border-slate-100">
            <Label className="text-xs font-bold uppercase text-slate-500 mb-2 block">Timeline Preview</Label>
            <div className="flex rounded-md overflow-hidden h-6">
              {segments.map((seg) => {
                const start = new Date(seg.start_date + 'T00:00:00');
                const end = new Date(seg.end_date + 'T00:00:00');
                const days = Math.max(1, (end - start) / (1000 * 60 * 60 * 24) + 1);
                return (
                  <div
                    key={seg._id}
                    className="flex items-center justify-center text-white text-[10px] font-bold truncate px-1"
                    style={{
                      backgroundColor: getStatusColor(seg.status),
                      flex: days,
                      minWidth: '20px'
                    }}
                    title={`${segLabel(seg)}: ${seg.start_date} to ${seg.end_date} (${days}d)`}
                  >
                    {days > 2 ? segLabel(seg) : ''}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Save as Template dialog */}
      <Dialog open={templateOpen} onOpenChange={setTemplateOpen}>
        <DialogContent className="max-w-sm" data-testid="save-template-dialog">
          <DialogHeader>
            <DialogTitle>Save as Schedule Template</DialogTitle>
            <DialogDescription>
              Saves this job's phases, durations, and dependencies as a reusable template (available under Schedule → Schedule Templates).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Template name</Label>
              <Input value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="e.g. Standard pump rebuild" data-testid="template-name-input" />
            </div>
            <Button onClick={saveAsTemplate} disabled={savingTemplate} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="confirm-save-template">
              <BookmarkPlus size={14} className="mr-1.5" /> {savingTemplate ? 'Saving...' : 'Save Template'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Browse & apply saved templates dialog */}
      <Dialog open={browseOpen} onOpenChange={setBrowseOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto overflow-x-hidden" data-testid="browse-templates-dialog">
          <DialogHeader>
            <DialogTitle>Schedule Templates</DialogTitle>
            <DialogDescription>
              Apply a saved template to this job. It replaces the current phase plan, anchored at the job's start date.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 min-w-0">
            {loadingTemplates ? (
              <p className="text-sm text-slate-500 text-center py-4">Loading templates…</p>
            ) : savedTemplates.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No saved templates yet. Use "Save as Template" or import a Microsoft Project XML file.</p>
            ) : (
              <div className="space-y-2">
                {savedTemplates.map(t => (
                  <div key={t.id} className="border border-slate-200 rounded-md p-3 flex items-center gap-3 min-w-0" data-testid={`browse-template-${t.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-800 truncate" title={t.name}>{t.name}</p>
                      <p className="text-xs text-slate-500 truncate">{t.tasks.length} tasks · {new Date(t.created_at).toLocaleDateString()} · {t.created_by_name}</p>
                    </div>
                    <Button size="sm" onClick={() => applySavedTemplate(t.id)} disabled={applyingTplId === t.id} className="bg-slate-900 hover:bg-slate-800 shrink-0" data-testid={`browse-apply-template-${t.id}`}>
                      {applyingTplId === t.id ? 'Applying…' : 'Apply'}
                    </Button>
                    <button onClick={() => deleteSavedTemplate(t.id)} className="text-slate-400 hover:text-red-500 shrink-0" data-testid={`browse-delete-template-${t.id}`}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
